import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { startProactiveRefresh, stopProactiveRefresh } from '../utils/axiosSetup';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Keep the session (JWT or Google) perpetually fresh while signed in so a
  // token never expires mid-shift and 401s a request at a track boundary.
  useEffect(() => {
    if (user) startProactiveRefresh();
    else stopProactiveRefresh();
    return () => stopProactiveRefresh();
  }, [user]);

  const checkAuth = useCallback(async () => {
    // Try Google session first, fall back to legacy JWT session
    try {
      const r = await axios.get(`${API}/google-auth/me`);
      setUser(r.data);
      setLoading(false);
      return;
    } catch { /* not signed in via Google */ }
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/google-auth/logout`); } catch { /* noop */ }
    try { await axios.post(`${API}/auth/logout`); } catch { /* noop */ }
    setUser(null);
  };

  const refreshUser = async () => { await checkAuth(); };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
